<template>
    <BaseModal>
        <template #header>
            <div class="prospus-modal__header--actions px-5 py-2">
                <img
                    src="/images/auth/logo-black.svg"
                    class="prospus-logo"
                    alt="kaamfu-logo-modal"
                />
                <span
                    class="prospus-close auth-modal-text-on-dark"
                    @click="$emit('close')"
                >
                    &times;
                </span>
            </div>
            <h3 class="prospus-title">Forgot Password</h3>
        </template>
        <div>
            <form
                v-if="!isPasswordResetLinkSent"
                class="prospus-form"
                @submit.prevent
                @keyup.enter.prevent="onSubmit"
            >
                <div class="prospus-email-wrap">
                    <label for="email"
                        >Please enter the email address associated with this
                        account</label
                    >
                    <div
                        class="input-wrapper prospus-has-validation-check"
                        :class="loginErrorMessages.email ? 'has-error' : ''"
                    >
                        <div class="input-wrapper__inner">
                            <button
                                type="button"
                                :class="[
                                    email && loginErrorMessages.email
                                        ? 'hint-mode'
                                        : '',
                                ]"
                                class="hint-mode"
                            >
                                <span class="material-symbols-rounded">{{
                                    emailValidateIcon
                                }}</span>
                            </button>
                            <input
                                ref="resetPwdEmail"
                                v-model="email"
                                type="email"
                                name="email"
                                title="email"
                                autofocus
                                autocomplete="false"
                                @focusout="loginErrorMessages.email = false"
                                @keypress="loginErrorMessages.email = ''"
                            />
                        </div>
                    </div>
                </div>
                <div class="prospus-submit prospus-flex-center-y">
                    <VBtn
                        v-if="!isRequestInProgress"
                        variant="plain"
                        color="icon"
                        class="px-3 text-none"
                        :style="{ fontWeight: 400, height: '29px' }"
                        @click.stop="$emit('close')"
                    >
                        Cancel
                    </VBtn>
                    <VBtn
                        variant="flat"
                        color="buttonBg"
                        hover-bg="bg-secondary"
                        class="px-3 text-none text-fontLight"
                        :style="{ fontWeight: 400, height: '29px' }"
                        :disabled="isRequestInProgress || !isEmailValid"
                        @click="sendPassordResetLink"
                    >
                        Submit
                    </VBtn>
                </div>
            </form>
            <div
                v-else
                class="prospus-form prospus-flex prospus-flex-column prospus-flex-center"
            >
                <div>
                    If there is an account associated with {{ email }}, please
                    check your inbox and follow the instructions to reset your
                    password.
                </div>
                <div class="prospus-mt-20">
                    <VBtn
                        v-if="!isRequestInProgress"
                        variant="flat"
                        color="buttonBg"
                        hover-bg="bg-secondary"
                        class="px-3 text-uppercase text-fontLight"
                        :style="{ fontWeight: 400, height: '29px' }"
                        @click.stop="$emit('close')"
                    >
                        Close
                    </VBtn>
                </div>
            </div>
        </div>
    </BaseModal>
</template>
<script>
import axios from "axios";
import BaseModal from "../Common/BaseModal.vue";
import { isValidEmail } from "../../../../../Admin/js/Utilities/emailValidator.js";
import utilities from "../../../Helpers/utilities";
export default {
    components: {
        BaseModal,
    },
    data() {
        return {
            email: "",
            isRequestInProgress: false,
            isPasswordResetLinkSent: false,
            responseErrorText: false,
            loginErrorMessages: {
                email: false,
                password: false,
            },
        };
    },
    computed: {
        emailValidateIcon() {
            if (this.email === "") return "mail";
            else if (this.verifyEmail()) return "task_alt";
            else return "highlight_off";
        },
        isEmailValid() {
            return isValidEmail(this.email);
        },
    },
    mounted() {
        this.setFocusToEmailField();
    },
    methods: {
        setFocusToEmailField() {
            const timeOutHandler = setTimeout(
                () => (
                    this.$refs.resetPwdEmail.focus(),
                    clearTimeout(timeOutHandler)
                ),
                400,
            );
        },
        async sendPassordResetLink() {
            if (this.isRequestInProgress) return;
            this.isRequestInProgress = true;
            this.responseErrorText = "";
            try {
                await axios.post("/forgot-password", { email: this.email });
                this.isPasswordResetLinkSent = true;
                this.responseErrorText = "";
                this.isRequestInProgress = true;
            } catch (error) {
                this.update422ErrorResponseMessage(error.response);
                utilities().showErrorSnackbar(
                    error.response?.data?.message ?? "Something went wrong",
                );
            }
            this.isRequestInProgress = false;
        },
        update422ErrorResponseMessage(response) {
            if (response?.status == 422)
                this.responseErrorText = response?.data?.message;
        },
        verifyEmail() {
            if (this.isEmailValid) return true;

            this.loginErrorMessages.email = true;
            return false;
        },
        onSubmit() {
            this.sendPassordResetLink();
        },
    },
};
</script>
<style lang="scss" scoped>
@import "../../../../sass/_theme-variables.sass";
@import "../../../../sass/_colors.sass";

.hint-mode {
    &:hover {
        color: $pauseDark !important;
        background-color: $page !important;
    }
}

/* Primary: exactly matches AuthIndex .btn */
.btn {
    background: $fontLight;
    color: $fontDark;
    padding: 8px 20px;
    border: none;
    font-size: 13.3px;
    font-weight: 500;
    border-radius: 20px;
    margin-left: 15px;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }
}

/* Secondary: exactly matches AuthIndex .buttons a */
.fp-cancel-link {
    color: $fontLight;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
}
</style>

<style lang="scss">
@import "../../../../sass/_theme-variables.sass";

/* Force form background to transparent (not dark blue) */
.prospus-modal__body .prospus-form {
    background: transparent !important;
    color: $fontLight !important;
}

.prospus-email-wrap label {
    color: $fontLight !important;
}

.prospus-email-wrap input {
    color: $fontLight !important;
    background: transparent !important;
    border-bottom: 2px solid $sectionBorder !important;
}

.prospus-email-wrap input:focus {
    border-bottom-color: $fontLight !important;
}

/* Ensure the email icon button is always visible */
.prospus-email-wrap .input-wrapper__inner button {
    color: $fontLight !important;
}
.response-error {
    margin-top: -26px;
    margin-left: 7px;
}
</style>

<style lang="scss">
@import "../../../../sass/_theme-variables.sass";

.prospus-modal__header--actions {
    background-color: $secondary !important;
}
</style>
